import React, { useState } from 'react'
import { useBenefits } from '../BenefitsProvider'
import { cn } from '~/lib/utils'
import MeetingInfo from '../ProfessionalServices/MeetingInfo'
import ResumeRevisions from './ResumeRevisions'
import RevisionDialog from './RevisionDialog'
import useStepInfo from './useStepInfo'
import { useTranslation } from '~/hooks/useTranslation'
import YnsIcon from '~/components/icons/YnsIcon'
import parse from 'html-react-parser'
import { apiPatch } from '~/util/api'
import { Button } from '~/components/_atoms/index'
import CandidateReviewFeedbackForm from './CandidateFeedback/CandidateReviewFeedbackForm'
import MessageCoachDialog from './MessageCoachDialog'
import CandidateChangeFormatDialog from '../ProfessionalServices/CandidateChangeFormatDialog'

const Subtitle = ({ stepKey }) => {
  const { t } = useTranslation()
  const { resumeReview, programLevel } = useBenefits()
  return (
    <div className="subtitle whitespace-pre-line break-words text-intoo-green-coachcta">
      {stepKey == 'finalFeedback' && resumeReview?.revision_declined
        ? t(`${programLevel}.steps.${stepKey}.title_no_revisions`, {
            date: new Date(
              resumeReview?.revision_declined_at,
            ).toLocaleDateString(),
          })
        : stepKey == 'finalFeedback' && resumeReview?.closed
          ? t(`${programLevel}.steps.${stepKey}.title_closed`, {
              date: new Date(resumeReview?.completed_at).toLocaleDateString(),
            })
          : (stepKey == 'scheduleMeeting' || stepKey == 'receiveFeedback') &&
              resumeReview?.meeting_via_calendly
            ? t(`${programLevel}.steps.${stepKey}.meeting_based_title`)
            : t(`${programLevel}.steps.${stepKey}.title`)}
    </div>
  )
}

const Step = ({
  stepKey,
  idx,
  steps,
  scheduleScrollRef,
  resumeReviewScrollRef,
  reviewCompleteScrollRef,
}) => {
  const { t } = useTranslation()
  const {
    resumeReview,
    coach,
    resumeRevisions,
    serviceRequestList,
    programLevel,
    currentCandidateRevision,
  } = useBenefits()
  const meetingViaCalendly = resumeReview?.meeting_via_calendly

  const statusToStepIndex = {
    submitted: 1,
    assigned: meetingViaCalendly ? 2 : 1,
    cancelled: 2,
    scheduled: 2,
    returned: meetingViaCalendly ? 4 : 3,
    revision_requested: meetingViaCalendly ? 4 : 3,
    revision_meeting_requested: meetingViaCalendly ? 4 : 3,
    revision_returned: meetingViaCalendly ? 6 : 5,
    final_revision_returned: meetingViaCalendly ? 6 : 5,
    completed: meetingViaCalendly ? 6 : 5,
    revision_declined: meetingViaCalendly ? 6 : 5,
    closed: meetingViaCalendly ? 6 : 5,
  }
  const stepComplete = () => {
    let index = idx
    if (stepKey === 'scheduleMeeting') {
      index -= 1
    } else if (!meetingViaCalendly && stepKey === 'receiveFeedback') {
      index -= 1
    }
    if (!resumeReview || !resumeReview?.status) return false
    const highestCompletedStep = statusToStepIndex[resumeReview?.status]
    return index <= highestCompletedStep
  }

  const showStepInfo = stepComplete()

  const calendlyEvent =
    stepKey === 'scheduleMeeting' &&
    serviceRequestList['ServiceRequest::ResumeReview']?.calendly_event

  const noShow = calendlyEvent?.service_request_no_show_id

  const stepInfo = useStepInfo({
    step: stepKey,
    resumeReview,
    resumeRevisions,
    coach,
    meetingViaCalendly,
    t,
    serviceRequestList,
  })

  const refMap = {
    scheduleMeeting: scheduleScrollRef,
    receiveFeedback: resumeReviewScrollRef,
    finalFeedback: reviewCompleteScrollRef,
  }

  const ref = refMap[stepKey] || null

  const completedClasses =
    idx === 0 || stepComplete(idx)
      ? 'bg-intoo-green-coachcta text-white'
      : 'bg-willow-brook text-intoo-green-coachcta'

  const showCandidateReviewFeedbackDialog =
    stepKey === 'finalFeedback' &&
    (resumeReview?.closed ||
      resumeReview?.status === 'final_revision_returned' ||
      resumeReview?.revision_declined) &&
    !resumeReview?.feedbacked

  const showRevisionFeedback =
    stepKey === 'finalFeedback' && resumeReview?.closed

  const showMeetingInfo =
    stepKey === 'scheduleMeeting' &&
    (resumeReview?.status === 'assigned' ||
      resumeReview?.status === 'scheduled' ||
      resumeReview?.status === 'cancelled')

  const descriptionKey =
    stepKey === 'finalFeedback' && resumeReview?.closed
      ? 'description_closed'
      : 'description'

  const coach_name = coach?.first_name

  const buttonText = noShow
    ? t(`${programLevel}.steps.scheduleMeeting.reschedule_meeting`)
    : t(`${programLevel}.steps.scheduleMeeting.schedule_meeting`, {
        coach_name,
      })

  const revisionClosed =
    resumeReview?.status === 'closed' && resumeRevisions?.length <= 1
  const askForRevisionsShowFeedback =
    stepKey === 'receiveFeedback' &&
    resumeRevisions?.length > 0 &&
    !revisionClosed

  const flexResumeReviewClosed =
    programLevel === 'Flex' && resumeReview?.status === 'closed'

  let nextStepShowsSwitchFormat = false
  if (
    stepKey === 'receiveFeedback' &&
    steps[idx + 1] === 'askForRevisions' &&
    !resumeReview?.meeting_via_calendly &&
    coach &&
    resumeReview?.status === 'revision_requested'
  ) {
    nextStepShowsSwitchFormat = true
  }

  const showSwitchFormatLink =
    (stepKey == 'receiveFeedback' &&
      !resumeReview?.meeting_via_calendly &&
      coach &&
      resumeReview?.status === 'assigned' &&
      !nextStepShowsSwitchFormat) ||
    (stepKey == 'askForRevisions' &&
      !currentCandidateRevision?.meeting_via_calendly &&
      coach &&
      resumeReview?.status === 'revision_requested')

  return (
    <div
      ref={ref}
      key={idx}
      className="relative mb-1 flex flex-col gap-1 md:static"
    >
      <div className="flex flex-col items-start gap-4 md:min-w-0 md:flex-row md:items-center">
        <div className="flex min-w-0 items-center gap-4">
          <div
            className={cn(
              'subtitle z-10 flex h-8 w-8 items-center justify-center rounded-full',
              completedClasses,
            )}
          >
            {idx + 1}
          </div>
          <Subtitle stepKey={stepKey} />
        </div>

        {showStepInfo && (
          <StepInfo
            step={stepKey}
            stepInfo={stepInfo}
            resumeReview={resumeReview}
          />
        )}
      </div>
      <div className="md:relative">
        {idx !== steps.length - 1 && (
          <div className="absolute left-3 top-0 h-full w-1 bg-willow-brook pb-1"></div>
        )}
        <div className="ml-12 pb-4">
          {parse(t(`${programLevel}.steps.${stepKey}.${descriptionKey}`))}
          {stepKey === 'chooseReviewType' && !flexResumeReviewClosed && (
            <div className="mt-4">
              <ChooseReviewType coach={coach} />
            </div>
          )}
          {showMeetingInfo && (
            <div className="mt-4">
              <MeetingInfo
                calendlyEvent={
                  serviceRequestList['ServiceRequest::ResumeReview']
                    ?.calendly_event
                }
                requestType="ServiceRequest::ResumeReview"
                buttonText={buttonText}
                requestComplete={resumeReview?.status == 'returned'}
                eventName={t('meeting')}
                cardTitle={t('card_title')}
                meetingViaCalendly={meetingViaCalendly}
                flexResumeReview={true}
              />
            </div>
          )}
          {askForRevisionsShowFeedback && (
            <RevisionFeedback stepKey={stepKey} revision={resumeRevisions[0]} />
          )}
          {stepKey === 'askForRevisions' && resumeRevisions?.length > 0 && (
            <ResumeRevisions stepKey={stepKey} revisions={resumeRevisions} />
          )}
          {showRevisionFeedback && (
            <div className="mt-4">
              <RevisionFeedback
                stepKey={stepKey}
                revision={resumeRevisions[resumeRevisions.length - 1]}
              />
            </div>
          )}
          {showCandidateReviewFeedbackDialog && (
            <div className="mt-4 items-center">
              <div className="font-medium">
                {t('CandidateReviewFeedback.we_love_feedback')}
              </div>
              <div className="mt-4">
                <CandidateReviewFeedbackForm
                  choices={t('CandidateReviewFeedback.choices')}
                  admonition={t('CandidateReviewFeedback.have_feedback')}
                  serviceRequestType="resume_review"
                  serviceRequestName={t(
                    'CandidateReviewFeedback.resume_review',
                  )}
                  serviceRequestId={resumeReview?.id}
                />
              </div>
            </div>
          )}
          {stepKey == 'submitResume' && resumeReview && (
            <SubmitResume resumeReview={resumeReview} />
          )}
          {showSwitchFormatLink && (
            <div className="mt-4">
              <CandidateChangeFormatDialog />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const StepInfo = ({ stepInfo }) => {
  if (!stepInfo) return null
  return (
    <div className="ml-12 bg-willow-brook p-2 font-medium text-intoo-green-coachcta md:ml-2">
      {stepInfo}
    </div>
  )
}

const SubmitResume = ({ resumeReview }) => {
  const { onScheduleClick, resumeRevisions, programLevel } = useBenefits()
  const { t } = useTranslation(programLevel)
  const handleUploadNewResumeClick = () => {
    onScheduleClick(
      'UploadNewResume',
      t(`steps.submitResume.upload_new_resume_modal_title`),
      resumeReview?.meeting_via_calendly,
    )
  }

  const hasResumeRevisions = resumeRevisions && resumeRevisions.length > 0

  if (!resumeReview?.resume_url) return null

  return (
    <LinkToDocument
      className="mt-4"
      text={t(`steps.submitResume.submitted_resume`)}
      documents={[
        {
          url: resumeReview?.resume_url,
          name: resumeReview?.resume_name,
        },
      ]}
    >
      {resumeReview && !hasResumeRevisions && (
        <Button
          onClick={handleUploadNewResumeClick}
          variant="link"
          title={t(`steps.submitResume.upload_new_resume`)}
        >
          <YnsIcon name="refresh" className="ml-2" />
        </Button>
      )}
    </LinkToDocument>
  )
}

const LinkToDocument = ({ className, text, documents, children }) => {
  return (
    <div className={cn('flex flex-col items-start md:flex-row', className)}>
      <div className="font-bold">{text}:</div>
      <div className="flex flex-col">
        {documents.map((doc, idx) => (
          <a
            key={`doc-${idx}`}
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-link hover:!underline md:ml-2"
          >
            {doc.name}
            <YnsIcon name="download" className="h-6 w-6" />
          </a>
        ))}
      </div>
      {children}
    </div>
  )
}

const RevisionFeedback = ({ stepKey, revision }) => {
  const {
    programLevel,
    resumeReview: { closed },
  } = useBenefits()
  const [showRevisionDialog, setShowRevisionDialog] = useState(false)
  const [messageCoachDialogIsOpen, setMessageCoachDialogIsOpen] =
    useState(false)
  const toggleShowRevisionDialog = () => {
    setShowRevisionDialog(!showRevisionDialog)
  }
  const openRevisionDialog = () => {
    setShowRevisionDialog(true)
    viewRevisionFeedback()
  }
  const { t } = useTranslation()
  const [viewed, setViewed] = useState(revision?.viewed)

  const viewRevisionFeedback = async () => {
    if (viewed) return
    let res
    try {
      res = await apiPatch(`/coach_services/resume_revisions/${revision?.id}`)
      setViewed(res?.data?.revision?.viewed)
    } catch (error) {
      console.error('Error viewing revision feedback:', error)
    }
  }

  const otherDocuments = revision?.other_documents || []

  return (
    <div className="mt-4 flex flex-col items-start gap-4">
      {revision && (
        <LinkToDocument
          className="flex items-start md:items-center"
          text={t(`${programLevel}.steps.${stepKey}.revision`)}
          documents={[
            {
              url: revision?.remote_content_url,
              name: revision?.remote_content,
            },
          ]}
        />
      )}
      {otherDocuments.length > 0 && (
        <LinkToDocument
          className="flex items-start"
          text={t('additional_documents')}
          documents={otherDocuments}
        />
      )}
      <div className="flex gap-4">
        <Button
          color="secondary"
          variant={viewed ? 'outline' : 'solid'}
          onClick={openRevisionDialog}
        >
          {t('view_feedback')}
        </Button>
        {stepKey === 'askForRevisions' && !closed && (
          <Button
            variant="outline"
            color="secondary"
            onClick={() => setMessageCoachDialogIsOpen(true)}
          >
            {t('ask_final_questions')}
          </Button>
        )}
        <MessageCoachDialog
          subject={t('final_questions_subject')}
          isOpen={messageCoachDialogIsOpen}
          onClose={() => setMessageCoachDialogIsOpen(false)}
        />
      </div>
      <RevisionDialog
        revision={revision}
        showRevisionDialog={showRevisionDialog}
        toggleShowRevisionDialog={toggleShowRevisionDialog}
      />
    </div>
  )
}

const ChooseReviewType = ({ coach }) => {
  const [messageCoachDialogIsOpen, setMessageCoachDialogIsOpen] =
    useState(false)

  const { t } = useTranslation()

  if (!coach) return null

  return (
    <>
      <Button
        variant="outline"
        color="secondary"
        onClick={() => setMessageCoachDialogIsOpen(true)}
      >
        {t('message_coach')}
      </Button>
      <MessageCoachDialog
        subject={t('CoachMessageThread.subject')}
        isOpen={messageCoachDialogIsOpen}
        onClose={() => setMessageCoachDialogIsOpen(false)}
      />
    </>
  )
}

export { Step, RevisionFeedback }
